// src/BrandingSettings.js
import React, { useState, useEffect, useRef } from 'react';
import TitleBar from './components/ui/TitleBar';
import InvoicePrint from './invoiceprint';

import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
const API_BASE = getApiBase();

// ── Mobile helper ──────────────────────────────────────────────────────────
const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth < 1024;

const PREDEFINED_PALETTES = [
  { name: 'Professional Blue', primary: '#2563eb', secondary: '#1e40af' },
  { name: 'Fresh Green', primary: '#10b981', secondary: '#059669' },
  { name: 'Bold Red', primary: '#ef4444', secondary: '#dc2626' },
  { name: 'Classic Black', primary: '#000000', secondary: '#374151' },
  { name: 'Modern Purple', primary: '#8b5cf6', secondary: '#7c3aed' },
  { name: 'Energetic Orange', primary: '#f97316', secondary: '#ea580c' },
  { name: 'Professional Teal', primary: '#14b8a6', secondary: '#0d9488' },
  { name: 'Elegant Navy', primary: '#1e3a8a', secondary: '#1e40af' }
];

const LAYOUT_OPTIONS = [
  { value: 'modern', label: 'MODERN' },
  { value: 'classic', label: 'CLASSIC' },
  { value: 'professional', label: 'PROFESSIONAL' },
  { value: 'bold', label: 'BOLD' }
];

const BrandingSettings = ({ navigate, isPhone }) => {
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [previewSnapshot, setPreviewSnapshot] = useState(null);

  const fileInputRef = useRef(null);

  const [settings, setSettings] = useState({
    logo: null,
    logo_filename: '',
    invoice_layout: 'modern',
    primary_color: '#2563eb',
    secondary_color: '#1e40af',
    tagline: ''
  });

  const [customColor, setCustomColor] = useState({
    primary: '#2563eb',
    secondary: '#1e40af'
  });

  // ── Responsive listener ────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', onResize);
    setIsMobile(getIsMobile()); // force correct value after mount
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load settings ──────────────────────────────────────────────────────────
  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/branding`, {
        headers: { ...getTenantHeaders() }
      });
      if (!res.ok) return;

      const data = await res.json();
      if (!data || Object.keys(data).length === 0) return;

      setSettings({
        logo: data.logoUrl || null,
        logo_filename: '',
        invoice_layout: data.invoiceLayout || data.invoice_layout || 'modern',
        primary_color: data.primary_color || '#2563eb',
        secondary_color: data.secondary_color || '#1e40af',
        tagline: data.tagline || ''
      });

      setCustomColor({
        primary: data.primary_color || '#2563eb',
        secondary: data.secondary_color || '#1e40af'
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // ── Save settings ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/branding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify({
          logoUrl: settings.logo,
          invoiceLayout: settings.invoice_layout,
          colorPalette: 'custom',
          primary_color: settings.primary_color,
          secondary_color: settings.secondary_color,
          tagline: settings.tagline
        })
      });

      if (!res.ok) throw new Error('Save failed');
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'BRANDING SAVED!' }
      }));
      await loadSettings();
    } catch (e) {
      alert('Failed to save: ' + (e.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Upload a PNG/JPG image.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setSettings(prev => ({
        ...prev,
        logo: event.target.result,
        logo_filename: file.name
      }));
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setSettings(prev => ({ ...prev, logo: null, logo_filename: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const applyPalette = (palette) => {
    setSettings(prev => ({
      ...prev,
      primary_color: palette.primary,
      secondary_color: palette.secondary
    }));
    setCustomColor({ primary: palette.primary, secondary: palette.secondary });
  };

  const applyCustomColor = () => {
    setSettings(prev => ({
      ...prev,
      primary_color: customColor.primary,
      secondary_color: customColor.secondary
    }));
  };

  const isActivePalette = (palette) =>
    settings.primary_color === palette.primary && settings.secondary_color === palette.secondary;

  const buildPreviewInvoice = (snap, businessData = {}) => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const invoiceDate = `${yyyy}-${mm}-${dd}`;

    const due = new Date(Date.now() + 7 * 86400000);
    const dyyyy = due.getFullYear();
    const dmm = String(due.getMonth() + 1).padStart(2, '0');
    const ddd = String(due.getDate()).padStart(2, '0');
    const dueDate = `${dyyyy}-${dmm}-${ddd}`;

    const branding = {
      logoUrl: snap.logo,
      invoiceLayout: snap.invoice_layout,
      invoice_layout: snap.invoice_layout,
      tagline: snap.tagline,
      primary_color: snap.primary_color,
      secondary_color: snap.secondary_color
    };

    return {
      invoice_number: 'PREVIEW-1001',
      status: 'unpaid',
      invoiceDate,
      dueDate,

      company: 'Sample Customer',
      customer_name: 'John Doe',
      address: '123 Main St',
      city: 'Des Moines',
      state: 'IA',
      zip: '50309',

      // ✅ real business info injected for preview header
      __isBrandingPreview: true,
      __businessName: String(businessData.businessName || businessData.business_name || ''),
      __businessAddress: String(businessData.address || ''),
      __businessCity: String(businessData.city || ''),
      __businessState: String(businessData.state || ''),
      __businessZip: String(businessData.zip || ''),
      __businessPhone: String(businessData.phone || ''),
      __businessEmail: String(businessData.email || ''),
      __businessWebsite: String(businessData.website || ''),

      subtotal: 250,
      discount: 25,
      stateTaxAmount: 10,
      countyTaxAmount: 5,
      total: 240,
      amount_paid: 0,
      balance_due: 240,

      items: [
        { id: 1, type: 'service', description: 'Service Call', qty: 1, rate: 125 },
        { id: 2, type: 'part', description: 'Replacement Part', qty: 1, rate: 125 },
        { id: 3, type: 'note', description: 'Thank you — we appreciate your business.' }
      ],
      payments: [],

      branding,
      branding_settings: branding,
      brandingSettings: branding,

      logoUrl: branding.logoUrl,
      invoiceLayout: branding.invoiceLayout,
      invoice_layout: branding.invoice_layout,
      tagline: branding.tagline,
      primary_color: branding.primary_color,
      secondary_color: branding.secondary_color
    };
  };

  const openPreview = async () => {
    const snap = {
      ...settings,
      primary_color: settings.primary_color || customColor.primary,
      secondary_color: settings.secondary_color || customColor.secondary
    };
    // ✅ fetch real business info so preview header shows correct subscriber data
    let businessData = {};
    try {
      const res = await fetch(`${API_BASE}/api/settings/business`, {
        headers: { ...getTenantHeaders() }
      });
      if (res.ok) businessData = await res.json();
    } catch (e) {
      console.error('Preview business fetch failed:', e);
    }
    snap.__businessData = businessData;
    setPreviewSnapshot(snap);
    setShowPreview(true);
  };

  const previewKey = previewSnapshot
    ? [
        previewSnapshot.invoice_layout,
        previewSnapshot.primary_color,
        previewSnapshot.secondary_color,
        previewSnapshot.tagline,
        previewSnapshot.logo ? 'HASLOGO' : 'NOLOGO'
      ].join('|')
    : 'nopreview';

  const styles = {
    page: { padding: isPhone ? '18px 4px' : (isMobile ? '18px' : '40px'), backgroundColor: '#E8E2DA', minHeight: '100vh' },
    wrap: { maxWidth: "1200px", padding: isPhone ? '0' : "0 12px", margin: '0 auto' },
    panel: {
      backgroundColor: '#E7B888',
      border: isMobile ? '6px solid black' : '10px solid black',
      padding: isPhone ? '15px 5px' : (isMobile ? '15px' : '24px'),
      marginBottom: '18px'
    },
    sectionTitle: { fontWeight: 900, textTransform: 'uppercase', fontSize: '18px', margin: '20px 0 14px 0', paddingTop: '20px', borderTop: '4px solid black' },
    label: { fontWeight: 900, textTransform: 'uppercase', fontSize: '13px', marginBottom: '6px', display: 'block' },
    input: { padding: '14px', border: '4px solid black', fontWeight: 900, width: '100%', fontSize: '16px', boxSizing: 'border-box' },
    select: { padding: '14px', border: '4px solid black', fontWeight: 900, width: '100%', fontSize: '16px', boxSizing: 'border-box', backgroundColor: 'white' },
    row1: { marginBottom: '12px' },
    row2: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '12px' },
    btn: { background: 'black', color: '#FFA500', border: '4px solid #E8761D', padding: '14px 20px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase', fontSize: '14px' },
    dangerBtn: { background: '#ff4444', color: 'white', border: '4px solid black', padding: '10px 16px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase', fontSize: '13px' },
    saveBtn: { background: 'black', color: '#FFA500', border: '5px solid #E8761D', padding: '16px 24px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase', fontSize: '18px', width: isMobile ? '100%' : 'auto' },
    paletteGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' },
    paletteCard: { border: '4px solid black', padding: '12px', cursor: 'pointer', backgroundColor: 'white', textAlign: 'center' },
    paletteCardActive: { border: '4px solid #2D5F3F', backgroundColor: '#E8F3EC' },
    paletteColors: { display: 'flex', gap: '8px', marginBottom: '8px', justifyContent: 'center' },
    colorBox: { width: '40px', height: '40px', border: '2px solid black' },
    paletteName: { fontWeight: 900, fontSize: '12px', textTransform: 'uppercase' },
    colorPicker: { width: '100%', height: '50px', border: '4px solid black', cursor: 'pointer' },
    logoPreview: { marginTop: '12px', padding: '14px', border: '4px solid black', backgroundColor: 'white', display: 'flex', alignItems: 'center', gap: '12px' },
    logoImg: { maxWidth: '200px', maxHeight: '100px', border: '2px solid black' },
    modal: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 6000, padding: '20px' },
    modalContent: { backgroundColor: 'white', border: '8px solid black', width: '100%', maxWidth: '980px', maxHeight: '90vh', overflowY: 'auto', padding: '18px' },
    modalHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
    loadingBox: { padding: '30px', textAlign: 'center', fontWeight: 900, fontSize: '16px' }
  };

  return (
    <div style={styles.page}>
      <style>{`
        .jf-btn-hover:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          transition: all 0.12s ease;
        }
        .jf-btn-hover:active { transform: translateY(1px); }
      `}</style>

      <div style={styles.wrap}>
        <TitleBar
          icon="🎨"
          title="BRANDING"
          accent="#E8761D"
          right={
            <button
              className="jf-btn-hover"
              onClick={() => navigate('/settings')}
              style={{
                background: 'black',
                color: '#FFA500',
                border: '5px solid #E8761D',
                padding: '14px',
                fontWeight: 900,
                cursor: 'pointer',
                textTransform: 'uppercase'
              }}
            >
              BACK TO SETTINGS
            </button>
          }
        />

        <div style={styles.panel}>
          {loading ? (
            <div style={styles.loadingBox}>⏳ LOADING BRANDING SETTINGS...</div>
          ) : (
            <>
              <h2 style={{ ...styles.sectionTitle, paddingTop: 0, borderTop: 'none' }}>LOGO</h2>

              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />

              <button className="jf-btn-hover" style={styles.btn} onClick={() => fileInputRef.current?.click()}>
                {settings.logo ? 'CHANGE LOGO' : 'UPLOAD LOGO'}
              </button>

              {settings.logo && (
                <>
                  <button className="jf-btn-hover" style={{ ...styles.dangerBtn, marginLeft: 10 }} onClick={removeLogo}>
                    REMOVE
                  </button>

                  <div style={styles.logoPreview}>
                    <img src={settings.logo} alt="Logo preview" style={styles.logoImg} />
                    <div style={{ fontWeight: 900, fontSize: '14px' }}>
                      {settings.logo_filename || '(saved logo)'}
                    </div>
                  </div>
                </>
              )}

              <h2 style={styles.sectionTitle}>COLORS</h2>

              <div style={styles.paletteGrid}>
                {PREDEFINED_PALETTES.map(palette => (
                  <div
                    key={palette.name}
                    style={{ ...styles.paletteCard, ...(isActivePalette(palette) ? styles.paletteCardActive : {}) }}
                    onClick={() => applyPalette(palette)}
                  >
                    <div style={styles.paletteColors}>
                      <div style={{ ...styles.colorBox, backgroundColor: palette.primary }} />
                      <div style={{ ...styles.colorBox, backgroundColor: palette.secondary }} />
                    </div>
                    <div style={styles.paletteName}>{palette.name}</div>
                  </div>
                ))}
              </div>

              <div style={styles.row2}>
                <div>
                  <label style={styles.label}>PRIMARY COLOR</label>
                  <input style={styles.input} value={customColor.primary} onChange={e => setCustomColor({ ...customColor, primary: e.target.value })} />
                  <input type="color" style={{ ...styles.colorPicker, marginTop: 8 }} value={customColor.primary} onChange={e => setCustomColor({ ...customColor, primary: e.target.value })} />
                </div>

                <div>
                  <label style={styles.label}>SECONDARY COLOR</label>
                  <input style={styles.input} value={customColor.secondary} onChange={e => setCustomColor({ ...customColor, secondary: e.target.value })} />
                  <input type="color" style={{ ...styles.colorPicker, marginTop: 8 }} value={customColor.secondary} onChange={e => setCustomColor({ ...customColor, secondary: e.target.value })} />
                </div>
              </div>

              <button className="jf-btn-hover" style={styles.btn} onClick={applyCustomColor}>
                APPLY CUSTOM COLORS
              </button>

              <h2 style={styles.sectionTitle}>TAGLINE</h2>
              <div style={styles.row1}>
                <label style={styles.label}>TAGLINE</label>
                <input style={styles.input} value={settings.tagline} onChange={e => setField('tagline', e.target.value)} maxLength="100" />
              </div>

              <h2 style={styles.sectionTitle}>INVOICE STYLE</h2>
              <div style={styles.row1}>
                <label style={styles.label}>SELECT STYLE</label>
                <select style={styles.select} value={settings.invoice_layout} onChange={e => setField('invoice_layout', e.target.value)}>
                  {LAYOUT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6 }}>
                <button className="jf-btn-hover" style={styles.btn} onClick={openPreview}>
                  PREVIEW
                </button>

                <button className="jf-btn-hover" style={styles.saveBtn} onClick={handleSave} disabled={saving}>
                  {saving ? 'SAVING...' : 'SAVE BRANDING'}
                </button>
              </div>
            </>
          )}
        </div>

        {showPreview && previewSnapshot && (
          <div style={styles.modal} onClick={() => setShowPreview(false)}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
              <div style={styles.modalHeaderRow}>
                <div style={{ fontWeight: 900, fontSize: 18, textTransform: 'uppercase' }}>
                  INVOICE PREVIEW — {String(previewSnapshot.invoice_layout).toUpperCase()}
                </div>

                <button className="jf-btn-hover" style={{ ...styles.btn, padding: '10px 14px' }} onClick={() => setShowPreview(false)}>
                  CLOSE
                </button>
              </div>

              <InvoicePrint key={previewKey} invoice={buildPreviewInvoice(previewSnapshot, previewSnapshot.__businessData || {})} usePreviewBranding={true} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandingSettings;
